import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Play, Pause, RotateCcw, CheckCircle2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTask } from "@/data/requests/task-requests";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import confetti from "canvas-confetti";

export function FocusScreen() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isActive, setIsActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const timerRef = useRef(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createTaskMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] }); // Update XP/Level
      toast({
        title: t("focus.completedTitle"),
        description:
          t("focus.rewards.completed") || t("focus.completedMessage"),
      });
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("focus.saveFailed"),
        variant: "destructive",
      });
    },
  });

  const handleComplete = useCallback(() => {
    const now = new Date();
    createTaskMutation.mutate({
      title: t("focus.taskTitle", {
        time: format(now, "HH:mm"),
        defaultValue: `Focus Session - ${format(now, "HH:mm")}`,
      }),
      description:
        t("focus.taskDescription") ||
        "Completed 25 minutes of deep work.",
      isCompleted: true,
      isImportant: true,
      xpReward: 50,
      dueDate: now.toISOString(),
    });
  }, [createTaskMutation]);

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isActive) {
      // Timer finished
      clearInterval(timerRef.current);
      setIsActive(false);
      setIsCompleted(true);
      handleComplete();
    }

    return () => clearInterval(timerRef.current);
  }, [isActive, timeLeft, handleComplete]);

  const toggleTimer = () => {
    setIsActive(!isActive);
  };

  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(25 * 60);
    setIsCompleted(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((25 * 60 - timeLeft) / (25 * 60)) * 100;

  return (
    <AppLayout
      title={t("focus.title")}
      description={t("focus.subtitle")}
    >
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md border-none shadow-none bg-transparent">
          <CardContent className="flex flex-col items-center space-y-8 pt-8">
            {/* Timer Display */}
            <div className="relative w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center">
              {/* Circular Progress Background */}
              <svg className="absolute w-full h-full transform -rotate-90" viewBox="0 0 256 256">
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-gray-200 dark:text-gray-700"
                />
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={2 * Math.PI * 120}
                  strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
                  className="text-primary transition-all duration-1000 ease-linear"
                  strokeLinecap="round"
                />
              </svg>
              
              <div className="text-5xl sm:text-6xl font-bold tracking-tighter font-mono">
                {formatTime(timeLeft)}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-6 mt-4">
              {!isCompleted ? (
                <>
                  <button
                    className="h-16 w-16 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center transition-colors shadow-lg"
                    onClick={toggleTimer}
                    aria-label={isActive ? "Pause timer" : "Start timer"}
                  >
                    {isActive ? (
                      <Pause className="h-8 w-8 text-white" />
                    ) : (
                      <Play className="h-8 w-8 text-white" style={{ marginLeft: '2px' }} />
                    )}
                  </button>
                  <button
                    className="h-16 w-16 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center transition-colors shadow-lg"
                    onClick={resetTimer}
                    aria-label="Reset timer"
                  >
                    <RotateCcw className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-500">
                  <div className="flex items-center gap-2 text-green-500 text-xl font-bold">
                    <CheckCircle2 className="h-8 w-8" />
                    <span>{t("focus.sessionCompletedShort")}</span>
                  </div>
                  <Button onClick={resetTimer}>{t("focus.startNewSession")}</Button>
                </div>
              )}
            </div>

            <div className="text-center text-muted-foreground max-w-xs px-4">
              {isActive ? (
                <p className="text-base sm:text-lg">{t("focus.stayFocused")}</p>
              ) : isCompleted ? (
                <p className="text-base sm:text-lg">{t("focus.takeBreak")}</p>
              ) : (
                <p className="text-base sm:text-lg">{t("focus.readyToFocus")}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
